import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  AudioLines,
  Check,
  ChevronRight,
  Download,
  FileAudio2,
  FileVideo2,
  Film,
  FolderOpen,
  HardDrive,
  Info,
  LoaderCircle,
  Menu,
  Play,
  RotateCcw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UploadCloud,
  WifiOff,
  X,
  Zap,
} from 'lucide-react'
import MediaPreview from './components/MediaPreview'
import SiteFooter from './components/SiteFooter'
import { canProcess, detectCapabilities } from './lib/capabilities'
import { localizeTool, locales, text, toolText, type Locale, type ToolId } from './i18n'
import { applySeoToDocument, relatedFor } from './seo'
import { allRoutes, legacyHashPath, pathFor, resolveRoute, type AppRoute } from './routes'
import { defaultsFor, toolMap, tools } from './tools'
import type { Capability, ProcessResult, ToolControl, ToolDefinition, ToolParams } from './types'
import type { EngineStatus } from './lib/ffmpeg'

type Stage = 'setup' | 'processing' | 'result' | 'error'
type MediaEngine = typeof import('./lib/ffmpeg')

function formatBytes(bytes: number, locale: Locale): string {
  const formatter = new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : locale, { maximumFractionDigits: 1 })
  if (bytes < 1024 * 1024) return `${formatter.format(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${formatter.format(bytes / 1024 / 1024)} MB`
  return `${formatter.format(bytes / 1024 / 1024 / 1024)} GB`
}

function isPlainLeftClick(event: React.MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

function ParameterControl({
  control,
  value,
  onChange,
  mtAvailable,
}: {
  control: ToolControl
  value: string | number | boolean
  onChange: (value: string | number) => void
  mtAvailable: boolean
}) {
  if (control.type === 'segmented') {
    return (
      <fieldset className="field segmented-field">
        <legend className="field-label">{control.label}</legend>
        <div className="segmented-control">
          {control.options?.map((option) => (
            <button
              key={option.value}
              type="button"
              className={String(value) === option.value ? 'is-selected' : ''}
              onClick={() => onChange(option.value)}
              aria-pressed={String(value) === option.value}
              disabled={Boolean(option.requiresMt && !mtAvailable)}
              title={option.requiresMt && !mtAvailable ? 'Requires cross-origin isolated multi-thread mode' : undefined}
            >
              {option.label}
            </button>
          ))}
        </div>
        {control.hint && <small>{control.hint}</small>}
      </fieldset>
    )
  }

  if (control.type === 'select') {
    return (
      <label className="field">
        <span className="field-label">{control.label}</span>
        <select value={String(value)} onChange={(event) => onChange(event.target.value)}>
          {control.options?.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {control.hint && <small>{control.hint}</small>}
      </label>
    )
  }

  if (control.type === 'range') {
    return (
      <label className="field range-field">
        <span className="field-label">
          {control.label}
          <output>{value}{control.unit}</output>
        </span>
        <input
          type="range"
          min={control.min}
          max={control.max}
          step={control.step}
          value={Number(value)}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="range-bounds">
          <span>{control.min}{control.unit}</span>
          <span>{control.max}{control.unit}</span>
        </span>
      </label>
    )
  }

  return (
    <label className="field">
      <span className="field-label">{control.label}</span>
      <span className="number-input">
        <input
          type="number"
          min={control.min}
          max={control.max}
          step={control.step}
          value={Number(value)}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        {control.unit && <span>{control.unit}</span>}
      </span>
      {control.hint && <small>{control.hint}</small>}
    </label>
  )
}

function CapabilityPanel({
  capabilities,
  locale,
  engineStatus,
  onClose,
}: {
  capabilities: Capability[]
  locale: Locale
  engineStatus: EngineStatus | null
  onClose: () => void
}) {
  const title = locale === 'en' ? 'What this browser can do' : locale === 'zh' ? '这台浏览器可以做什么' : 'このブラウザで利用できる機能'
  const intro = locale === 'en'
    ? 'This check reads browser capabilities only. It never scans your files.'
    : locale === 'zh'
      ? '检测只读取浏览器能力，不会扫描你的文件。'
      : 'ブラウザ機能だけを確認し、ファイルを読み取りません。'
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="capability-modal" role="dialog" aria-modal="true" aria-labelledby="capability-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-heading">
          <div>
            <p className="eyebrow">{text(locale, 'capabilities')}</p>
            <h2 id="capability-title">{title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={text(locale, 'closeMenu')}><X size={20} /></button>
        </div>
        <p className="modal-intro">{intro}</p>
        <div className="capability-list">
          {capabilities.map((capability) => (
            <div className="capability-row" key={capability.name}>
              <span className={capability.supported ? 'cap-dot is-supported' : 'cap-dot'} aria-hidden="true" />
              <div><strong>{capability.name}</strong><span>{capability.detail}</span></div>
              <b>{capability.supported ? (locale === 'en' ? 'Available' : locale === 'zh' ? '可用' : '利用可能') : (locale === 'en' ? 'Unavailable' : locale === 'zh' ? '不可用' : '利用不可')}</b>
            </div>
          ))}
        </div>
        <div className="capability-note">
          <Info size={18} />
          <span>{locale === 'en' ? 'The compatible single-thread FFmpeg core uses WebAssembly and a Worker.' : locale === 'zh' ? '兼容优先的单线程 FFmpeg 核心使用 WebAssembly 与 Worker。' : '互換性重視のシングルスレッド FFmpeg は WebAssembly と Worker を使用します。'}</span>
        </div>
        <div className={`engine-status engine-${engineStatus?.mode ?? (capabilities[4]?.supported ? 'mt' : 'st')}`}>
          <strong>
            {engineStatus?.mode === 'mt' || (!engineStatus && capabilities[4]?.supported)
              ? (locale === 'en' ? 'Multi-thread mode ready' : locale === 'zh' ? '多线程模式可用' : 'マルチスレッド利用可能')
              : (locale === 'en' ? 'Single-thread compatibility mode' : locale === 'zh' ? '单线程兼容模式' : 'シングルスレッド互換モード')}
          </strong>
          {engineStatus?.reason && <span>{engineStatus.reason}</span>}
          {engineStatus?.hardwareConcurrency && engineStatus.hardwareConcurrency < 4 && (
            <span>{locale === 'en' ? 'Fewer than four logical cores; MT gains may be limited.' : locale === 'zh' ? '逻辑核心少于 4 个，多线程收益可能有限。' : '論理コアが4未満のためMT効果が小さい場合があります。'}</span>
          )}
        </div>
        <button className="primary-button full-button" onClick={onClose}>{locale === 'en' ? 'Done' : locale === 'zh' ? '知道了' : '閉じる'}</button>
      </section>
    </div>
  )
}

function HomePage({
  locale,
  navigate,
}: {
  locale: Locale
  navigate: (route: AppRoute, event?: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  const videoTools = tools.filter((tool) => tool.kind === 'video')
  const audioTools = tools.filter((tool) => tool.kind === 'audio')
  return (
    <>
      <section className="home-hero">
        <p className="eyebrow">{text(locale, 'homeEyebrow')}</p>
        <h1>{text(locale, 'homeTitle')}</h1>
        <p>{text(locale, 'homeIntro')}</p>
        <div className="home-actions">
          <a className="primary-button" href={pathFor(locale, 'video-compress')} onClick={(event) => navigate({ locale, toolId: 'video-compress' }, event)}>{text(locale, 'browseVideo')}</a>
          <a className="secondary-button" href={pathFor(locale, 'audio-extract')} onClick={(event) => navigate({ locale, toolId: 'audio-extract' }, event)}>{text(locale, 'browseAudio')}</a>
        </div>
      </section>
      <section className="home-tool-catalog" aria-labelledby="all-tools">
        <h2 id="all-tools">{text(locale, 'allTools')}</h2>
        {[{ label: text(locale, 'videoTools'), values: videoTools }, { label: text(locale, 'audioTools'), values: audioTools }].map((group) => (
          <div className="catalog-group" key={group.label}>
            <h3>{group.label}</h3>
            <div className="catalog-grid">
              {group.values.map((rawTool) => {
                const localized = localizeTool(rawTool, locale)
                const Icon = localized.icon
                return (
                  <a key={localized.id} href={pathFor(locale, localized.id as ToolId)} onClick={(event) => navigate({ locale, toolId: localized.id as ToolId }, event)}>
                    <Icon size={20} />
                    <span><strong>{localized.name}</strong><small>{localized.description}</small></span>
                    <ChevronRight size={17} />
                  </a>
                )
              })}
            </div>
          </div>
        ))}
      </section>
      <section className="trust-grid" aria-label={text(locale, 'privacy')}>
        <article><ShieldCheck size={20} /><div><strong>{text(locale, 'fileNoUpload')}</strong><span>{text(locale, 'fileNoUploadDetail')}</span></div></article>
        <article><LoaderCircle size={20} /><div><strong>{text(locale, 'cancellable')}</strong><span>{text(locale, 'cancellableDetail')}</span></div></article>
        <article><Download size={20} /><div><strong>{text(locale, 'ownerSave')}</strong><span>{text(locale, 'ownerSaveDetail')}</span></div></article>
      </section>
    </>
  )
}

function ToolSeoContent({
  locale,
  toolId,
  navigate,
}: {
  locale: Locale
  toolId: ToolId
  navigate: (route: AppRoute, event?: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  const copy = toolText(locale, toolId)
  const related = relatedFor(locale, toolId)
  return (
    <section className="seo-content" aria-label={`${copy.name} ${text(locale, 'howItWorks')}`}>
      <div className="seo-content-grid">
        <article>
          <h2>{text(locale, 'howItWorks')}</h2>
          <p>{copy.description}</p>
          <ol><li>{text(locale, 'step1')}</li><li>{text(locale, 'step2')}</li><li>{text(locale, 'step3')}</li></ol>
        </article>
        <article>
          <h2>{text(locale, 'formats')}</h2>
          <dl><div><dt>{text(locale, 'input')}</dt><dd>{copy.inputFormats}</dd></div><div><dt>{text(locale, 'output')}</dt><dd>{copy.outputFormats}</dd></div></dl>
        </article>
        <article>
          <h2>{text(locale, 'privacy')}</h2>
          <p>{text(locale, 'privacyText')}</p>
        </article>
        <article>
          <h2>{text(locale, 'limitations')}</h2>
          <p>{copy.limitation}</p>
        </article>
      </div>
      <div className="related-tools">
        <h2>{text(locale, 'relatedTools')}</h2>
        <div>
          {related.map((item) => (
            <a key={item.id} href={pathFor(locale, item.id)} onClick={(event) => navigate({ locale, toolId: item.id }, event)}>
              {item.name}<ChevronRight size={15} />
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

export default function App({
  initialRoute,
  siteUrl,
}: {
  initialRoute: AppRoute
  siteUrl: string
}) {
  const [route, setRoute] = useState(initialRoute)
  const locale = route.locale
  const rawTool = route.toolId ? (toolMap.get(route.toolId) ?? tools[0]) : null
  const tool = rawTool ? localizeTool(rawTool, locale) : null
  const [params, setParams] = useState<ToolParams>(() => rawTool ? defaultsFor(rawTool) : {})
  const [files, setFiles] = useState<File[]>([])
  const [stage, setStage] = useState<Stage>('setup')
  const [isDragging, setIsDragging] = useState(false)
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [result, setResult] = useState<ProcessResult | null>(null)
  const [resultUrl, setResultUrl] = useState('')
  const [error, setError] = useState('')
  const [engineLog, setEngineLog] = useState('')
  const [showCapabilities, setShowCapabilities] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [capabilities, setCapabilities] = useState<Capability[]>([])
  const [engineStatus, setEngineStatus] = useState<EngineStatus | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const durationFileRef = useRef('')
  const mediaEngineRef = useRef<MediaEngine | null>(null)

  const isSupported = useMemo(() => capabilities.length > 0 && canProcess(capabilities), [capabilities])
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
  const hasEnoughFiles = tool ? files.length >= (tool.minFiles ?? 1) : false

  useEffect(() => {
    setCapabilities(detectCapabilities(locale))
    const onPopState = () => {
      const next = resolveRoute(window.location.pathname)
      if (next) setRoute(next)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [locale])

  useEffect(() => {
    applySeoToDocument(route, siteUrl)
  }, [route, siteUrl])

  useEffect(() => () => {
    if (resultUrl) URL.revokeObjectURL(resultUrl)
    mediaEngineRef.current?.cancelProcessing()
  }, [resultUrl])

  useEffect(() => {
    if (stage !== 'processing' || tool?.id !== 'video-interpolate') return
    const timer = window.setTimeout(() => {
      setProgressMessage(locale === 'en'
        ? 'Still calculating — this operation is compute-intensive and can be cancelled.'
        : locale === 'zh'
          ? '仍在计算中——补帧计算量很大，可随时取消。'
          : '計算を継続中です。負荷の高い処理のため、いつでもキャンセルできます。')
    }, 60000)
    return () => window.clearTimeout(timer)
  }, [locale, progress, stage, tool?.id])

  const resetWorkspace = (nextTool: ToolDefinition | null) => {
    if (resultUrl) URL.revokeObjectURL(resultUrl)
    durationFileRef.current = ''
    setParams(nextTool ? defaultsFor(nextTool) : {})
    setFiles([])
    setResult(null)
    setResultUrl('')
    setError('')
    setStage('setup')
    setMobileMenuOpen(false)
  }

  const navigate = (nextRoute: AppRoute, event?: React.MouseEvent<HTMLAnchorElement>) => {
    if (event && (!isPlainLeftClick(event) || event.currentTarget.target === '_blank')) return
    event?.preventDefault()
    if (stage === 'processing') return
    const nextRawTool = nextRoute.toolId ? toolMap.get(nextRoute.toolId) ?? null : null
    resetWorkspace(nextRawTool)
    setRoute(nextRoute)
    const nextPath = pathFor(nextRoute.locale, nextRoute.toolId)
    if (window.location.pathname !== nextPath) window.history.pushState({}, '', nextPath)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const acceptFiles = (incoming: File[]) => {
    if (!incoming.length || !tool) return
    setFiles(tool.multiple ? incoming : incoming.slice(0, 1))
    setError('')
    setStage('setup')
  }

  const handleMediaDuration = (fileKey: string, duration: number) => {
    if (tool?.id !== 'video-trim' || durationFileRef.current === fileKey) return
    durationFileRef.current = fileKey
    setParams((current) => ({ ...current, start: 0, end: Number(duration.toFixed(2)) }))
  }

  const validate = (): string | null => {
    if (!tool) return null
    if (!isSupported) return locale === 'en' ? 'This browser needs WebAssembly and Worker support.' : locale === 'zh' ? '当前浏览器缺少 WebAssembly 或 Worker。' : 'このブラウザでは WebAssembly または Worker を利用できません。'
    if (!hasEnoughFiles) return tool.multiple
      ? (locale === 'en' ? `Choose at least ${tool.minFiles ?? 2} files.` : locale === 'zh' ? `请至少选择 ${tool.minFiles ?? 2} 个文件。` : `${tool.minFiles ?? 2} 件以上のファイルを選択してください。`)
      : (locale === 'en' ? 'Choose a file first.' : locale === 'zh' ? '请先选择一个文件。' : '先にファイルを選択してください。')
    if ((tool.id === 'video-trim' || tool.id === 'audio-trim') && Number(params.end) <= Number(params.start)) {
      return locale === 'en' ? 'The end time must be after the start time.' : locale === 'zh' ? '结束时间必须晚于开始时间。' : '終了時間は開始時間より後にしてください。'
    }
    if (tool.id === 'video-watermark' && !files[1]?.type.startsWith('image/')) return locale === 'en' ? 'The second file must be a PNG or JPG watermark.' : locale === 'zh' ? '第二个文件需要是 PNG 或 JPG 水印图片。' : '2件目には PNG/JPG の透かし画像を選択してください。'
    if (tool.id === 'video-subtitles' && !files[1]?.name.toLowerCase().endsWith('.srt')) return locale === 'en' ? 'The second file must be an SRT subtitle.' : locale === 'zh' ? '第二个文件需要是 .srt 字幕文件。' : '2件目には SRT 字幕を選択してください。'
    if (totalBytes > 2 * 1024 * 1024 * 1024) return locale === 'en' ? 'The browser build does not process inputs over 2 GB.' : locale === 'zh' ? '浏览器版本暂不处理超过 2 GB 的输入。' : 'ブラウザ版では 2 GB を超える入力を処理できません。'
    return null
  }

  const run = async () => {
    if (!tool) return
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      setStage('error')
      return
    }
    if (resultUrl) URL.revokeObjectURL(resultUrl)
    setResult(null)
    setResultUrl('')
    setError('')
    setEngineLog('')
    setProgress(0)
    setProgressMessage(locale === 'en' ? 'Preparing…' : locale === 'zh' ? '正在准备…' : '準備中…')
    setStage('processing')
    try {
      const engine = await import('./lib/ffmpeg')
      mediaEngineRef.current = engine
      const nextResult = await engine.processMedia(
        tool.id,
        files,
        params,
        (ratio, message) => {
          setProgress(ratio)
          setProgressMessage(locale === 'zh' ? message : locale === 'en'
            ? (ratio > 0.9 ? 'Finalizing the result…' : ratio > 0.04 ? 'Processing locally…' : 'Starting the local media engine…')
            : (ratio > 0.9 ? '結果を仕上げています…' : ratio > 0.04 ? '端末内で処理しています…' : 'ローカルメディアエンジンを起動中…'))
        },
        (message) => setEngineLog(message),
        (status) => setEngineStatus(status),
      )
      const url = URL.createObjectURL(nextResult.blob)
      setResult(nextResult)
      setResultUrl(url)
      setStage('result')
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : ''
      if (message.toLowerCase().includes('terminate')) {
        setError(locale === 'en' ? 'Task cancelled and temporary files released.' : locale === 'zh' ? '任务已取消，临时文件已经释放。' : '処理をキャンセルし、一時ファイルを解放しました。')
      } else if (caught instanceof Error && caught.name === 'MediaValidationError') {
        setError(caught.message)
      } else {
        const detail = engineLog ? ` ${engineLog.slice(-180)}` : ''
        setError((locale === 'en' ? 'Processing failed. Check the input format or convert it to MP4 / MP3 first.' : locale === 'zh' ? '无法完成处理。请确认输入格式，或先转换为 MP4 / MP3。' : '処理できませんでした。入力形式を確認するか、先に MP4 / MP3 へ変換してください。') + detail)
      }
      setStage('error')
    }
  }

  const cancel = () => {
    mediaEngineRef.current?.cancelProcessing()
    setProgress(0)
    setError(locale === 'en' ? 'Task cancelled and temporary files released.' : locale === 'zh' ? '任务已取消，临时文件已经释放。' : '処理をキャンセルし、一時ファイルを解放しました。')
    setStage('error')
  }

  const saveResult = async () => {
    if (!result) return
    if (window.showSaveFilePicker) {
      try {
        const extension = `.${result.fileName.split('.').pop()}`
        const handle = await window.showSaveFilePicker({
          suggestedName: result.fileName,
          types: [{ description: 'Processed media', accept: { [result.mime]: [extension] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(result.blob)
        await writable.close()
        return
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') return
      }
    }
    const anchor = document.createElement('a')
    anchor.href = resultUrl
    anchor.download = result.fileName
    anchor.click()
  }

  const previewType = result?.mime.startsWith('image/') ? 'image' : result?.mime.startsWith('audio/') ? 'audio' : 'video'
  const localizedVideoTools = tools.filter((item) => item.kind === 'video').map((item) => localizeTool(item, locale))
  const localizedAudioTools = tools.filter((item) => item.kind === 'audio').map((item) => localizeTool(item, locale))

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="mobile-menu-button" onClick={() => setMobileMenuOpen(true)} aria-label={text(locale, 'chooseTool')}><Menu size={21} /></button>
        <a className="brand" href={pathFor(locale)} onClick={(event) => navigate({ locale, toolId: null }, event)} aria-label={text(locale, 'brandHome')}>
          <span className="brand-mark">F</span>
          <span><strong>Frame Local</strong><small>PRIVATE MEDIA KIT</small></span>
        </a>
        <div className="local-signal">
          <span className="signal-pulse" aria-hidden="true" />
          <span><strong>{text(locale, 'localOnly')}</strong><small>{text(locale, 'noUpload')}</small></span>
        </div>
        <div className="top-actions">
          <nav className="locale-switcher" aria-label={text(locale, 'languages')}>
            {locales.map((nextLocale) => (
              <a
                key={nextLocale}
                className={nextLocale === locale ? 'is-active' : ''}
                href={pathFor(nextLocale, route.toolId)}
                hrefLang={nextLocale === 'zh' ? 'zh-CN' : nextLocale}
                lang={nextLocale === 'zh' ? 'zh-CN' : nextLocale}
                onClick={(event) => navigate({ locale: nextLocale, toolId: route.toolId }, event)}
              >
                {nextLocale === 'en' ? 'EN' : nextLocale === 'zh' ? '中文' : '日本語'}
              </a>
            ))}
          </nav>
          <button className="capability-button" onClick={() => setShowCapabilities(true)}><Zap size={17} />{text(locale, 'capabilities')}</button>
        </div>
      </header>

      <aside className={`sidebar ${mobileMenuOpen ? 'is-open' : ''}`}>
        <div className="sidebar-mobile-heading">
          <span>{text(locale, 'chooseTool')}</span>
          <button className="icon-button" onClick={() => setMobileMenuOpen(false)} aria-label={text(locale, 'closeMenu')}><X size={20} /></button>
        </div>
        <nav aria-label={text(locale, 'mediaTools')}>
          {[{ label: text(locale, 'videoTools'), icon: Film, values: localizedVideoTools }, { label: text(locale, 'audioTools'), icon: AudioLines, values: localizedAudioTools }].map((group, groupIndex) => {
            const GroupIcon = group.icon
            return (
              <div key={group.label}>
                <p className={`nav-label ${groupIndex ? 'audio-label' : ''}`}><GroupIcon size={14} />{group.label}<span>{group.values.length}</span></p>
                <div className="tool-nav">
                  {group.values.map((item) => {
                    const Icon = item.icon
                    return (
                      <a
                        key={item.id}
                        href={pathFor(locale, item.id as ToolId)}
                        className={item.id === tool?.id ? 'tool-nav-item is-active' : 'tool-nav-item'}
                        aria-current={item.id === tool?.id ? 'page' : undefined}
                        aria-disabled={stage === 'processing'}
                        onClick={(event) => navigate({ locale, toolId: item.id as ToolId }, event)}
                      >
                        <Icon size={17} strokeWidth={1.8} /><span>{item.shortName}</span><ChevronRight size={15} className="nav-arrow" />
                      </a>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </nav>
        <div className="sidebar-foot"><WifiOff size={16} /><span>{text(locale, 'offlineReady')}</span></div>
      </aside>
      {mobileMenuOpen && <button className="sidebar-backdrop" aria-label={text(locale, 'closeMenu')} onClick={() => setMobileMenuOpen(false)} />}

      <main className="workspace">
        {!tool || !route.toolId ? (
          <HomePage locale={locale} navigate={navigate} />
        ) : (
          <>
            <nav className="breadcrumbs" aria-label="Breadcrumb">
              <a href={pathFor(locale)} onClick={(event) => navigate({ locale, toolId: null }, event)}>{text(locale, 'home')}</a>
              <ChevronRight size={14} />
              <span aria-current="page">{tool.name}</span>
            </nav>
            <section className="tool-hero">
              <div className={`tool-icon tool-icon-${tool.kind}`}><tool.icon size={25} strokeWidth={1.7} /></div>
              <div className="tool-heading">
                <div className="eyebrow-row">
                  <span className="eyebrow">{tool.kind === 'video' ? text(locale, 'videoTool') : text(locale, 'audioTool')} / {tool.shortName}</span>
                  <span className={`support-badge support-${tool.support}`}>{tool.support === 'stable' ? (locale === 'en' ? 'Lightweight' : locale === 'zh' ? '轻量处理' : '軽量処理') : tool.support === 'standard' ? (locale === 'en' ? 'Standard' : locale === 'zh' ? '标准处理' : '標準処理') : (locale === 'en' ? 'Experimental' : locale === 'zh' ? '实验能力' : '実験機能')}</span>
                </div>
                <h1>{tool.name}</h1>
                <p>{tool.description}</p>
              </div>
            </section>

            <section className="privacy-rail">
              <div className="rail-item is-current"><span>01</span><div><strong>{text(locale, 'localRead')}</strong><small>{text(locale, 'browserMemory')}</small></div></div><i />
              <div className="rail-item"><span>02</span><div><strong>{text(locale, 'deviceProcess')}</strong><small>{text(locale, 'wasmWorker')}</small></div></div><i />
              <div className="rail-item"><span>03</span><div><strong>{text(locale, 'saveResult')}</strong><small>{text(locale, 'yourDisk')}</small></div></div>
              <div className="rail-lock"><ShieldCheck size={19} />{text(locale, 'zeroUploads')}</div>
            </section>

            {tool.limitation && <div className={`limit-note limit-${tool.support}`}><AlertTriangle size={18} /><div><strong>{tool.support === 'limited' ? text(locale, 'beforeStart') : text(locale, 'capabilityNote')}</strong><span>{tool.limitation}</span></div></div>}

            <section className="work-card">
              {stage === 'processing' ? (
                <div className="processing-state">
                  <div className="progress-visual" style={{ '--progress': `${Math.round(progress * 360)}deg` } as React.CSSProperties}><div><strong>{Math.round(progress * 100)}</strong><span>%</span></div></div>
                  <p className="eyebrow">{text(locale, 'processing')}</p><h2>{progressMessage}</h2><p>{text(locale, 'keepOpen')}</p>
                  <div className="progress-track"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                  <button className="cancel-button" onClick={cancel}><X size={17} />{text(locale, 'cancel')}</button>
                </div>
              ) : stage === 'result' && result ? (
                <div className="result-state">
                  <div className="result-heading"><span className="success-icon"><Check size={26} /></span><div><p className="eyebrow">{text(locale, 'complete')}</p><h2>{text(locale, 'resultLocal')}</h2><p>{formatBytes(result.blob.size, locale)} · {(result.elapsedMs / 1000).toFixed(1)}s</p></div></div>
                  <div className="result-preview">{previewType === 'image' && <img src={resultUrl} alt="" />}{previewType === 'audio' && <audio src={resultUrl} controls />}{previewType === 'video' && <video src={resultUrl} controls playsInline />}</div>
                  <div className="result-actions"><button className="primary-button" onClick={saveResult}><Save size={18} />{text(locale, 'saveDevice')}</button><button className="secondary-button" onClick={() => { setResult(null); setResultUrl(''); setStage('setup') }}><RotateCcw size={17} />{text(locale, 'adjust')}</button></div>
                </div>
              ) : (
                <>
                  <div
                    className={`drop-zone ${isDragging ? 'is-dragging' : ''} ${files.length ? 'has-files' : ''}`}
                    onDragEnter={(event) => { event.preventDefault(); setIsDragging(true) }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={(event) => { if (event.currentTarget === event.target) setIsDragging(false) }}
                    onDrop={(event) => { event.preventDefault(); setIsDragging(false); acceptFiles(Array.from(event.dataTransfer.files)) }}
                  >
                    <input ref={fileInputRef} type="file" accept={tool.accept} multiple={Boolean(tool.multiple)} onChange={(event) => { acceptFiles(Array.from(event.target.files ?? [])); event.target.value = '' }} tabIndex={-1} aria-hidden="true" />
                    {!files.length ? (
                      <><span className="drop-icon"><UploadCloud size={27} strokeWidth={1.7} /></span><h2>{tool.filePrompt ?? (tool.kind === 'video' ? text(locale, 'dropVideo') : text(locale, 'dropAudio'))}</h2><p>{text(locale, 'chooseFileHint')}</p><button className="secondary-button" onClick={() => fileInputRef.current?.click()}><FolderOpen size={18} />{text(locale, 'chooseFiles')}</button><small>{tool.accept.replaceAll(',', ' · ')}</small></>
                    ) : (
                      <div className="file-stack">
                        <div className="file-stack-heading"><div><p className="eyebrow">{files.length} {text(locale, 'selectedFiles')}</p><h2>{formatBytes(totalBytes, locale)} · {text(locale, 'localVisible')}</h2></div><button className="text-button" onClick={() => fileInputRef.current?.click()}><FolderOpen size={16} />{tool.multiple ? text(locale, 'reselect') : text(locale, 'replace')}</button></div>
                        <div className="file-list">{files.map((file, index) => <div className="file-row" key={`${file.name}-${file.lastModified}-${index}`}><span className="file-type-icon">{file.type.startsWith('audio/') ? <FileAudio2 size={20} /> : <FileVideo2 size={20} />}</span><div><strong>{file.name}</strong><span>{formatBytes(file.size, locale)} · {file.type || text(locale, 'unknownMedia')}</span></div>{files.length > 1 && <em>{index + 1}</em>}<button className="icon-button" onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))} aria-label={`${text(locale, 'remove')} ${file.name}`}><Trash2 size={17} /></button></div>)}</div>
                      </div>
                    )}
                  </div>

                  {files[0] && <MediaPreview file={files[0]} fileCount={files.length} tool={tool} params={params} locale={locale} onParamChange={(key, value) => setParams((current) => ({ ...current, [key]: value }))} onDuration={handleMediaDuration} />}

                  {tool.controls.length > 0 && <div className="parameter-section"><div className="section-heading"><SlidersHorizontal size={18} /><div><h3>{text(locale, 'parameters')}</h3><p>{tool.outputHint}</p></div></div><div className="control-grid">{tool.controls.map((control) => tool.id === 'video-trim' && (control.key === 'start' || control.key === 'end') ? null : <ParameterControl key={control.key} control={control} value={params[control.key]} mtAvailable={Boolean(capabilities[4]?.supported)} onChange={(value) => setParams((current) => ({ ...current, [control.key]: value }))} />)}</div></div>}

                  {stage === 'error' && <div className="error-banner" role="alert"><AlertTriangle size={19} /><div><strong>{text(locale, 'taskFailed')}</strong><span>{error}</span></div><button className="icon-button" onClick={() => { setError(''); setStage('setup') }} aria-label={text(locale, 'closeMenu')}><X size={18} /></button></div>}
                  <div className="action-row"><div className="action-summary"><HardDrive size={18} /><span><strong>{engineStatus?.mode === 'mt' ? (locale === 'en' ? 'Multi-thread engine' : locale === 'zh' ? '多线程引擎' : 'マルチスレッド') : engineStatus?.mode === 'st' ? (locale === 'en' ? 'Single-thread compatibility mode' : locale === 'zh' ? '单线程兼容模式' : 'シングルスレッド互換') : text(locale, 'localOnly')}</strong><small>{engineStatus?.reason ?? tool.outputHint}</small></span></div><button className="primary-button process-button" onClick={run}><Play size={18} fill="currentColor" />{text(locale, 'start')}</button></div>
                </>
              )}
            </section>

            <section className="trust-grid" aria-label={text(locale, 'privacy')}>
              <article><ShieldCheck size={20} /><div><strong>{text(locale, 'fileNoUpload')}</strong><span>{text(locale, 'fileNoUploadDetail')}</span></div></article>
              <article><LoaderCircle size={20} /><div><strong>{text(locale, 'cancellable')}</strong><span>{text(locale, 'cancellableDetail')}</span></div></article>
              <article><Download size={20} /><div><strong>{text(locale, 'ownerSave')}</strong><span>{text(locale, 'ownerSaveDetail')}</span></div></article>
            </section>
            <ToolSeoContent locale={locale} toolId={route.toolId} navigate={navigate} />
          </>
        )}
        <SiteFooter locale={locale} onOpenCapabilities={() => setShowCapabilities(true)} />
      </main>
      {showCapabilities && <CapabilityPanel capabilities={capabilities} locale={locale} engineStatus={engineStatus} onClose={() => setShowCapabilities(false)} />}
    </div>
  )
}

export function routeFromBrowser(): AppRoute {
  const legacy = legacyHashPath(window.location.hash)
  if (legacy) {
    window.history.replaceState({}, '', legacy)
    return resolveRoute(legacy) ?? { locale: 'en', toolId: null }
  }
  return resolveRoute(window.location.pathname) ?? { locale: 'en', toolId: null }
}

export const generatedRouteCount = allRoutes().length
